import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { UUID } from "crypto";
import api from "@/app/api";
import { feeKeys } from "../keys/fees";
import type { FeeCreate, FeeUpdate, FeeFilters } from "@/app/api/fees/client";

export function useFees(filters?: FeeFilters) {
  return useQuery({
    queryKey: feeKeys.list(filters),
    queryFn: () => api.fees.list(filters),
  });
}

export function useFee(id: UUID | null) {
  return useQuery({
    queryKey: feeKeys.detail(id!),
    queryFn: () => api.fees.get(id!),
    enabled: !!id,
  });
}

export function useFeesByReference(
  referenceType: string | null,
  referenceId: UUID | null,
) {
  return useQuery({
    queryKey: feeKeys.byReference(referenceType!, referenceId!),
    queryFn: () => api.fees.getByReference(referenceType!, referenceId!),
    enabled: !!referenceType && !!referenceId,
  });
}

export function useFeesTotalByReference(
  referenceType: string | null,
  referenceId: UUID | null,
) {
  return useQuery({
    queryKey: feeKeys.totalByReference(referenceType!, referenceId!),
    queryFn: () => api.fees.getTotalByReference(referenceType!, referenceId!),
    enabled: !!referenceType && !!referenceId,
  });
}

export function useCreateFee() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: FeeCreate) => api.fees.create(data),
    onSuccess: (fee) => {
      queryClient.invalidateQueries({ queryKey: feeKeys.all });
      queryClient.invalidateQueries({
        queryKey: feeKeys.byReference(fee.reference_type, fee.reference_id),
      });
      queryClient.invalidateQueries({
        queryKey: feeKeys.totalByReference(
          fee.reference_type,
          fee.reference_id,
        ),
      });
    },
  });
}

export function useUpdateFee() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: UUID; data: FeeUpdate }) =>
      api.fees.update(id, data),
    onSuccess: (fee) => {
      queryClient.invalidateQueries({ queryKey: feeKeys.all });
      queryClient.invalidateQueries({
        queryKey: feeKeys.detail(fee.id),
      });
    },
  });
}

export function useDeleteFee() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: UUID) => api.fees.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: feeKeys.all });
    },
  });
}

export function useDeleteFeesByReference() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      referenceType,
      referenceId,
    }: {
      referenceType: string;
      referenceId: UUID;
    }) => api.fees.deleteByReference(referenceType, referenceId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: feeKeys.all });
    },
  });
}
